import { ArrowLeft } from 'lucide-preact'
import { useCallback, useState } from 'preact/hooks'
import { text } from '#contract'
import type { SaveSlotView } from '#contract'
import { useClient } from '../../app/client-context.ts'
import { useBackOverride, useScreenStack } from '../../app/screen-stack.ts'
import { useView } from '../../app/use-view.ts'
import { Button } from '../../components/button.tsx'
import { ScreenPanel } from '../../components/screen-panel.tsx'
import { useLocale, useT } from '../../i18n/index.ts'
import styles from './save-slots.module.css'
import { SlotCard } from './slot-card.tsx'
import { savedWhen } from './slot-text.ts'

type Mode = 'new' | 'load'

/** Asked before a save is lost: overwriting it with a new game, or deleting it. */
interface Question {
  readonly kind: 'overwrite' | 'delete'
  readonly slot: SaveSlotView
}

/** New game: choose the slot it's saved in. */
export function NewGameScreen() {
  return <SaveSlotsScreen mode="new" />
}

/** Load game: choose a save. */
export function LoadGameScreen() {
  return <SaveSlotsScreen mode="load" />
}

/**
 * The three save slots as cards. New game takes any slot (a used one asks first); Load takes
 * a save that reads. Every used slot has a bin, which asks first. Back closes the question
 * before the screen.
 */
function SaveSlotsScreen({ mode }: { mode: Mode }) {
  const t = useT()
  const client = useClient()
  const { back } = useScreenStack()
  const slots = useView((view) => view.saves.slots)
  const [question, setQuestion] = useState<Question | null>(null)
  const cancel = useCallback(() => {
    setQuestion(null)
  }, [])
  useBackOverride(question ? cancel : null)

  if (question) return <Confirm question={question} onCancel={cancel} />

  const choose = (slot: SaveSlotView): void => {
    if (mode === 'load') client?.saves.load(slot.slot)
    else if (slot.state === 'empty') client?.saves.newGame(slot.slot)
    else setQuestion({ kind: 'overwrite', slot })
  }
  const usable = (slot: SaveSlotView): boolean => mode === 'new' || slot.state === 'ok'
  const first = slots.find(usable)

  return (
    <ScreenPanel
      title={t(text(mode === 'new' ? 'saves.newGame' : 'saves.loadGame'))}
      footer={
        <Button
          variant="ghost"
          icon={ArrowLeft}
          sound="back"
          autoFocus={first === undefined}
          onClick={back}
        >
          {t(text('saves.back'))}
        </Button>
      }
    >
      <ul class={styles.slots}>
        {slots.map((slot) => (
          <SlotCard
            key={slot.slot}
            slot={slot}
            disabled={!usable(slot)}
            autoFocus={slot === first}
            onChoose={() => {
              choose(slot)
            }}
            {...(slot.state === 'empty'
              ? {}
              : {
                  onDelete: () => {
                    setQuestion({ kind: 'delete', slot })
                  },
                })}
          />
        ))}
      </ul>
    </ScreenPanel>
  )
}

function Confirm({ question, onCancel }: { question: Question; onCancel: () => void }) {
  const t = useT()
  const locale = useLocale()
  const client = useClient()
  const { kind, slot } = question
  const when = slot.state === 'ok' ? savedWhen(locale, slot) : null
  const answer = (): void => {
    if (kind === 'delete') client?.saves.delete(slot.slot)
    else client?.saves.newGame(slot.slot)
    onCancel()
  }

  return (
    <ScreenPanel
      title={t(
        text(kind === 'delete' ? 'saves.deleteTitle' : 'saves.overwriteTitle', { n: slot.slot }),
      )}
      footer={
        <>
          <Button variant="ghost" sound="back" autoFocus onClick={onCancel}>
            {t(text('saves.cancel'))}
          </Button>
          <Button variant="danger" onClick={answer}>
            {t(text(kind === 'delete' ? 'saves.deleteAction' : 'saves.overwriteAction'))}
          </Button>
        </>
      }
    >
      <p>{t(when ? text('saves.lost', { when }) : text('saves.lostThis'))}</p>
    </ScreenPanel>
  )
}
